import {useRef} from 'react';
import * as React from 'react';
import {FormattedMessage} from 'react-intl';

import {Margin, SITE, LinkStyle, HeadlineLevel} from 'features/ui/enums';
import {ShowingsType} from 'features/search/enums';
import {StageStatus} from 'features/search/filters/enums';
import {useModal} from 'features/modal/hooks/useModal';
import {Showing as ShowingType} from "features/search/types";
import {useAuth} from "features/authentication/hooks/useAuth";
import {getCurrentSite} from 'features/services/utils/env-helper';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {showingIsVisible} from 'features/services/utils/showing-helpers';
import {sanitizeHtml} from 'features/services/utils/sanitize-html';

import './style.scss';

import {A} from 'components/atoms/LinksAndButtons/A';
import {P} from 'components/atoms/Text/P';
import {LoginForm} from 'components/forms/LoginForm';
import {Showing} from 'components/molecules/Showings/Showings/Showing';
import {ShowingsModal} from "components/modals/General/ShowingsModal";
import {Headline} from '@atoms/Text/Headline';

interface Props {
    showings?: ShowingType[];
    stageName: string;
    stageId: string;
    stageStatus: string;
    smarta?: boolean;
    showingText?: string;
    showingLinkText?: string;
    showingLinkUrl?: string;
}

export const Showings: React.FunctionComponent<Props> = ({
    showings, showingText, showingLinkText, showingLinkUrl,
    stageName, stageId, stageStatus, smarta,
}) => {
    const { open } = useModal();
    const showingRef = useRef<HTMLAnchorElement>(null);
    const loginLinkRef = useRef<HTMLAnchorElement>(null);
    const {user, isLoggedIn} = useAuth();
    const userIsVip = user ? user.isVip : false;

    const getHeadingText = (containsVip) => {
        const site = getCurrentSite();
        if(containsVip && user?.isVip) return getFormattedMessage('jm.components.Showings.vipShowing');
        if(site !== SITE.FI || (site === SITE.FI && stageStatus !== StageStatus.PLANNED)) return getFormattedMessage('jm.components.Showings.showings');
        return "Suunnitteilla";
    };

    const onShowingClick = (showing: ShowingType) => (_e: React.SyntheticEvent) => {
        open(
            <ShowingsModal
                showing={showing}
                stageId={stageId}
                stageName={stageName}
                url={window.location.href}
            />,
            { fullscreen: false, triggerElementId: `showing-${showing.id}`, ariaLabel: getFormattedMessage('jm.components.ShowingsModal.welcomeShowing') }
        );
    };

    const onLogin = (e) => {
        e.preventDefault();
        open(
            <LoginForm hideTypeToggle />,
            { fullscreen: false, triggerElementId: 'logInForVipShowing', ariaLabel: getFormattedMessage('jm.components.LoginForm.ariaLabel') }
        );
    };
    const today = new Date();
    let filteredShowings: ShowingType[] = [];

    if (showings) {
        filteredShowings = showings.filter(x => showingIsVisible(x, userIsVip));

        filteredShowings = filteredShowings.filter(x => new Date(x.endDate) > today).sort(function (a, b) {
            if (a.startDate > b.startDate) return 1;
            if (a.startDate < b.startDate) return -1;
            return 0;
        }).slice(0, user?.isVip ? 3 : 2);
    }

    const containsVip = showings && showings.some(x => x.type === ShowingsType.VIP);
    const showVipLogin = stageStatus === StageStatus.VIP && (!isLoggedIn || (isLoggedIn && !user?.isVip)) && containsVip && showings && showings.length > 0;

    return (
        <div className={smarta ? "showings" : "showings dark-bg"} data-test-id="showings">
            <Headline level={HeadlineLevel.H2} margin={Margin.L} center tabable>{getHeadingText(containsVip)}</Headline>

            {filteredShowings.length > 0 &&
                filteredShowings.map((showing, index) => (
                    <Showing
                        key={`showing-${showing.id}-${index}`}
                        id={`showing-${showing.id}`}
                        onClick={onShowingClick}
                        showing={showing}
                        vipOnly={containsVip}
                        data-test-id={containsVip && showing.type === ShowingsType.VIP ? "vip-showing" : "showing"} 
                        isVip={showing.type === ShowingsType.VIP}
                        smarta={smarta}
                        ref={showingRef}
                    />
                ))
            }

            {((!showingText || showingText === "") && filteredShowings.length === 0 && !showVipLogin) &&
                <P className="showings--contact" center>
                    {(getCurrentSite() !== SITE.FI || (getCurrentSite() === SITE.FI && stageStatus !== StageStatus.PLANNED)) &&
                        <FormattedMessage id="jm.components.Showings.noShowings" />
                    }
                    {getCurrentSite() === SITE.FI && stageStatus === StageStatus.PLANNED &&
                        <React.Fragment>Ilmoita kiinnostuksestasi ja kuulet lisää ennen ennakkomarkkinoinnin alkamista!</React.Fragment>
                    }
                </P>
            }

            {showVipLogin &&
                <div className="showings__text">
                    <P>
                        <FormattedMessage id="jm.components.Showings.plannedVIPShowingsStart" /> <A href="#" onClick={onLogin} variant={!smarta ? LinkStyle.DARK_BG : LinkStyle.DEFAULT} ref={loginLinkRef} id='logInForVipShowing' title={getFormattedMessage('jm.components.Showings.plannedVIPShowingsLogin')} ><FormattedMessage id="jm.components.Showings.plannedVIPShowingsLogin" /></A> <FormattedMessage id="jm.components.Showings.plannedVIPShowingsEnd" />
                    </P>
                </div>
            }

            {showingText && showingText !== "" && stageStatus !== StageStatus.PLANNED &&
                <div className="showings__text" dangerouslySetInnerHTML={{__html: sanitizeHtml(showingText)}} />
            }
            {(showingLinkText && showingLinkUrl) &&
                <div className="showings__link">
                    <A href={showingLinkUrl} route title={showingLinkText}>{showingLinkText}</A>
                </div>
            }
        </div>
    );
};
